import React, { useState, useEffect, useRef } from 'react';
import { useProfileStore } from '../../state/profileStore';
import axios from 'axios';
import CustomerCardList from '../customers/CustomerCardList';
import { UploadCloud } from 'lucide-react';
import UploadModal from '../customers/UploadModal';
import { StarRating } from '../profile/customersurveyform';
import AllReviewsList from '../customers/AllReviewsList';
import GoogleImportButton from '../customers/GoogleImportButton';

function CustomerInfo() {
  // Individual selectors — see userprofile.jsx for context. Whole-store
  // destructure caused re-render cascades + 429 rate limits on /rating-summary.
  const profile = useProfileStore((s) => s.profile);
  const refreshProfile = useProfileStore((s) => s.refreshProfile);
  const [expandedCustomerId, setExpandedCustomerId] = useState(null);
  const [editedCustomers, setEditedCustomers] = useState({});
  const [customers, setCustomers] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewFilter, setReviewFilter] = useState(null); // null | 'not_completed' | 'not_replied'
    // Infinite scroll (reviews column)
  const [visibleCount, setVisibleCount] = useState(10);  // start with 10
  const sentinelRef = useRef(null);                      // sentinel element at bottom of list
  const isFetchingRef = useRef(false);                   // throttles repeated triggers
  const [isLoadingRatings, setIsLoadingRatings] = useState(true);
  const [googleRefreshModal, setGoogleRefreshModal] = useState(null); // null | 'cooldown' | 'importing' | 'first_import'
  const [googleCooldownInfo, setGoogleCooldownInfo] = useState(null);
  const [googleRefreshError, setGoogleRefreshError] = useState('');
  const [ratingData, setRatingData] = useState({
    u_total_review_score: 0,
    u_review_count: 0,
    u_avg_rating: null
  });
  useEffect(() => {
  console.log("Edited Customers:", editedCustomers);
}, [editedCustomers]);

  useEffect(() => {
    if (profile?.u_id) {
      refreshProfile(profile.u_id);
    }
  }, [profile?.u_id, refreshProfile]);
  useEffect(() => {
  console.log('User in useEffect now:', profile);
  if (!profile?.u_id) return; // <- Wait until user is loaded


const fetchCustomers = async () => {
  try {
    const res = await fetch(`/api/customer-cards?user_id=${profile.u_id}`, { credentials: 'include' });
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    const data = await res.json();
    console.log("FETCHED CUSTOMERS:", data);
    setCustomers(Array.isArray(data) ? data : []);
  } catch (error) {
    console.error('Failed to load customers:', error);
    setCustomers([]); // Set empty array on error
  }
};

  fetchCustomers();
  fetchReviews();
  fetchRatingSummary(profile.u_id);
}, [profile?.u_id]);

// ── Listen for Help Center "Add Your First Customer" event ──
useEffect(() => {
  const handler = () => handleAddNewCustomer();
  window.addEventListener('customers:addNew', handler);
  return () => window.removeEventListener('customers:addNew', handler);
}, []);

// === DERIVED METRICS ===
// Filter real customers (exclude "Imported Reviews")
const realCustomers = customers.filter(
  (c) => c.customer_firstname?.toLowerCase() !== 'imported'
);
const totalCustomers = realCustomers.length;

// Separate reviews into eHandshake and Google
const ehandshakeReviews = reviews.filter(
  (r) => r.customersurvey_google_origin !== 'google_places'
);
const googleReviews = reviews.filter(
  (r) => r.customersurvey_google_origin === 'google_places'
);

// Review Requests Sent = all eHandshake reviews
const totalReviews = ehandshakeReviews.length;

// Reviews Completed = eHandshake reviews with a completed date
const reviewsCompleted = ehandshakeReviews.filter(
  (r) => r.date
).length;

// Reviews Not Completed = eHandshake reviews with NO completed date
const reviewsNotCompleted = ehandshakeReviews.filter(
  (r) => !r.date
).length;

// Calculate not replied for both eHandshake and Google reviews
const ehandshakeNotReplied = ehandshakeReviews.filter(
  (r) => r.date && !r.customersurvey_user_reply
).length;

const googleNotReplied = googleReviews.filter(
  (r) => !r.customersurvey_user_reply
).length;

const totalNotReplied = ehandshakeNotReplied + googleNotReplied;
const totalReplied = (reviewsCompleted - ehandshakeNotReplied) + (googleReviews.length - googleNotReplied);


// Add this right after your fetchCustomers function
// In customerinfo.jsx, update the fetchReviews function to match the working pattern:

const fetchReviews = async () => {
  try {
    console.log(`Fetching reviews from /api/profile/${profile.u_id}/reviews`);
    const res = await axios.get(
      `/api/profile/${profile.u_id}/reviews?include_pending=true`,
      { withCredentials: true }
    );
    
    console.log('Received reviews:', {
      status: res.status,
      data: res.data
    });

    // Ensure we have the reply data
    const reviewsWithReplyStatus = res.data.map(review => ({
      ...review,
      hasReply: !!review.customersurvey_user_reply
    }));
    
    setReviews(reviewsWithReplyStatus);
  } catch (err) {
    console.error('Review fetch failed:', {
      error: err.message,
      status: err.response?.status,
      data: err.response?.data
    });
    setReviews([]);
  }
};

// Also update the fetchRatingSummary function for consistency:
const fetchRatingSummary = async (userId) => {
  try {
    const response = await axios.get(
      `/api/profile/${userId}/rating-summary`,
      { withCredentials: true }
    );
    
    setRatingData({
      u_total_review_score: parseFloat(response.data.u_total_review_score),
      u_review_count: response.data.total_reviews || 0,
      u_avg_rating: parseFloat(response.data.u_avg_rating),
      ehandshake_count: response.data.ehandshake_review_count || 0,
      google_count: response.data.google_review_count || 0
    });
  } catch (err) {
    console.error('Failed to load ratings:', err);
    setRatingData({
      u_total_review_score: 0,
      u_review_count: 0,
      u_avg_rating: null,
      ehandshake_count: 0,
      google_count: 0
    });
  }
};

  // === ACTION HANDLERS ===
const handleCancelEdit = (id) => {
  console.log("Cancel edit for customer:", id);
  // logic to exit editing mode (if needed)
};

const handleSave = async (id) => {
  const updated = editedCustomers[id];
  if (!updated) return;

  try {
    await axios.post('/api/update-customer', {
      customer_id: id,
      customer_firstname: updated.firstName,
      customer_lastname: updated.lastName,
      customer_email: updated.email,
      customer_phone: updated.phone,
      customer_zipcode: updated.zip
    });


    setExpandedCustomerId(null);
    console.log("Customer updated successfully");
  } catch (error) {
    console.error("Failed to update customer:", error);
  }
};

const handleFieldChange = (index, field, value) => {
  setEditedCustomers((prev) => ({
    ...prev,
    [index]: {
      ...prev[index],
      [field]: value
    }
  }));
};

const openResponseModal = (id) => {
  console.log("Open response modal for customer:", id);
  // logic to show modal
};
const handleAddNewCustomer = () => {
  const newCustomerId = `new_${Date.now()}`;
  const blankCustomer = {
    customer_id: newCustomerId,
    customer_firstname: '',
    customer_lastname: '',
    customer_email: '',
    customer_phone: '',
    customer_zipcode: '',
    customer_created_at: new Date().toISOString(),
    customer_last_interaction: null,
    customer_completed_at: null,
  };

  setCustomers(prev => [blankCustomer, ...prev]);
  setEditedCustomers(prev => ({
    ...prev,
    [newCustomerId]: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      zip: '',
    },
  }));
};

  const handleReviewsScroll = (e) => {
    const el = e.currentTarget;
    if (!el) return;

    // Early trigger to start loading around ~7 reviews worth of scroll (tune if needed)
    const earlyTriggerPx = 900;

    const scrolledPastEarlyPoint = el.scrollTop >= earlyTriggerPx;
    const nearBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - earlyTriggerPx;

    if (
      (scrolledPastEarlyPoint || nearBottom) &&
      !isFetchingRef.current &&
      visibleCount < reviews.length
    ) {
      isFetchingRef.current = true;

      setVisibleCount((prev) => {
        const remaining = reviews.length - prev;
        return prev + Math.min(10, remaining);
      });

      setTimeout(() => {
        isFetchingRef.current = false;
      }, 200);
    }
  };

  // Shared handler after any Google import succeeds (first import or refresh)
  const handleGoogleImportSuccess = async () => {
    try {
      await fetchReviews();
      await fetchRatingSummary(profile.u_id);
      if (profile?.u_id) {
        await refreshProfile(profile.u_id);
      }
    } catch (err) {
      console.error('[customerinfo] post-import refresh error:', err);
    }
    setGoogleRefreshModal(null);
  };

  const handleGoogleRefreshClick = async () => {
    setGoogleRefreshError('');
    try {
      const statusRes = await axios.get('/api/import-google-reviews/status', { withCredentials: true });
      const data = statusRes.data;

      if (!data.has_imported) {
        // Never imported before — show the URL input modal
        setGoogleRefreshModal('first_import');
        return;
      }

      if (!data.can_import) {
        // Cooldown active
        setGoogleCooldownInfo({
          last_import: data.last_import,
          next_import: data.next_import,
        });
        setGoogleRefreshModal('cooldown');
        return;
      }

      // Good to go — refresh using stored place_id
      setGoogleRefreshModal('importing');
      const refreshRes = await axios.post('/api/import-google-reviews/refresh', {}, { withCredentials: true });

      if (refreshRes.data.success) {
        await handleGoogleImportSuccess();
      }
    } catch (err) {
      if (err.response?.data?.cooldown) {
        setGoogleCooldownInfo({
          last_import: err.response.data.last_import,
          next_import: err.response.data.next_import,
        });
        setGoogleRefreshModal('cooldown');
      } else {
        setGoogleRefreshError(err.response?.data?.error || 'Failed to refresh Google reviews');
        setGoogleRefreshModal('error');
      }
    }
  };

    if (!profile) return <div></div>;
  return (
    <div className="container mx-auto w-4/5 pt-6 flex flex-col">
      <div className="flex flex-col md:flex-row gap-3 flex-grow">
        {/* Left Column - User Info */}
        <div className="w-full md:w-[24%] bg-white rounded-lg p-4 shadow-[0_0_20px_rgba(0,0,150,0.1)] h-fit">
          <div className="space-y-4">

            {/* === PROFILE CARD: Photo + Name === */}
            <div
              className="rounded-lg p-4 bg-white transition-all duration-200"
              style={{
                boxShadow: '0 4px 12px -2px rgba(0, 174, 247, 0.25), 0 2px 4px -1px rgba(0, 174, 247, 0.15), inset 0 -8px 12px -8px rgba(0, 174, 247, 0.1)',
                border: '1px solid rgba(0, 174, 247, 0.2)',
                borderBottomColor: 'rgba(0, 174, 247, 0.35)',
                borderBottomWidth: '2px'
              }}
            >
              {/* User Profile Picture */}
              <div className="relative w-28 h-28 mx-auto mb-3">
                <div className="w-28 h-28 rounded-full border-2 border-[#00aef7]/30 overflow-hidden">
                  <img
                    src={
                      profile.u_profile_picture
                        ? `${process.env.REACT_APP_API_URL}${profile.u_profile_picture}`
                        : '/images/default-avatar.png'
                    }
                    className="w-full h-full object-cover"
                    alt="Profile"
                  />
                </div>
              </div>

              {/* Name */}
              <h2 className="text-lg font-bold text-gray-800 text-center">
                {profile?.u_first_name} {profile?.u_last_name}
              </h2>
            </div>

            {/* === eHANDSHAKE & GOOGLE STATS CARD === */}
            <div
              className="rounded-lg p-3 bg-white transition-all duration-200"
              style={{
                boxShadow: '0 2px 6px -1px rgba(156, 163, 175, 0.15), 0 1px 2px -1px rgba(156, 163, 175, 0.1), inset 0 -4px 6px -4px rgba(156, 163, 175, 0.08)',
                border: '1px solid rgba(156, 163, 175, 0.2)',
                borderBottomColor: 'rgba(156, 163, 175, 0.3)',
                borderBottomWidth: '2px'
              }}
            >
              <div className="text-center text-sm text-gray-700">
                {/* eHandshake Section */}
                <div className="mb-4 flex flex-col items-center">
                  <img
                    src="/images/ehandshake-icon.png"
                    alt="e"
                    className="w-6 h-6 mb-2"
                  />
                  <div className="grid grid-cols-[max-content_1fr] gap-x-4 text-left">
                    <div className="whitespace-nowrap"><strong>Customers:</strong></div>
                    <div className="whitespace-nowrap text-right">{totalCustomers}</div>

                    <div className="whitespace-nowrap"><strong>Requests Sent:</strong></div>
                    <div className="whitespace-nowrap text-right">{totalReviews}</div>

                    <div className="whitespace-nowrap"><strong>Completed:</strong></div>
                    <div className="whitespace-nowrap text-right">{reviewsCompleted}</div>

                    <div className="whitespace-nowrap">
                      <strong
                        className={`cursor-pointer hover:text-red-600 ${reviewFilter === 'not_completed' ? 'text-red-600 underline' : ''}`}
                        onClick={() => setReviewFilter(reviewFilter === 'not_completed' ? null : 'not_completed')}
                      >Not Completed:</strong>
                    </div>
                    <div className="whitespace-nowrap text-right">
                      <span
                        className={`cursor-pointer hover:text-red-600 ${reviewFilter === 'not_completed' ? 'text-red-600 font-bold' : ''}`}
                        onClick={() => setReviewFilter(reviewFilter === 'not_completed' ? null : 'not_completed')}
                      >{reviewsNotCompleted}</span>
                    </div>

                    <div className="whitespace-nowrap"><strong>Replied To:</strong></div>
                    <div className="whitespace-nowrap text-right">{totalReplied}</div>

                    <div className="whitespace-nowrap">
                      <strong
                        className={`cursor-pointer hover:text-red-600 ${reviewFilter === 'not_replied' ? 'text-red-600 underline' : ''}`}
                        onClick={() => setReviewFilter(reviewFilter === 'not_replied' ? null : 'not_replied')}
                      >Not Replied To:</strong>
                    </div>
                    <div className="whitespace-nowrap text-right">
                      <span
                        className={`cursor-pointer hover:text-red-600 ${reviewFilter === 'not_replied' ? 'text-red-600 font-bold' : ''}`}
                        onClick={() => setReviewFilter(reviewFilter === 'not_replied' ? null : 'not_replied')}
                      >{totalNotReplied}</span>
                    </div>
                  </div>
                </div>

                {/* Google Section */}
                <div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 48 48"
                    className="mx-auto mb-2"
                  >
                    <path
                      fill="#4285F4"
                      d="M24 9.5c3.34 0 5.63 1.44 6.93 2.65l5.08-4.91C32.67 4.02 28.74 2 24 2 14.91 2 7.54 7.88 4.64 15.35l5.89 4.57C12.12 13.12 17.56 9.5 24 9.5z"
                    />
                    <path
                      fill="#34A853"
                      d="M46.5 24c0-1.53-.14-2.99-.4-4.4H24v8.33h12.7c-.55 3.02-2.19 5.58-4.7 7.31l7.38 5.71C43.98 36.9 46.5 30.97 46.5 24z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M10.53 28.74a14.4 14.4 0 0 1 0-9.47l-5.89-4.57a23.953 23.953 0 0 0 0 18.61l5.89-4.57z"
                    />
                    <path
                      fill="#EA4335"
                      d="M24 46c6.48 0 11.91-2.15 15.88-5.86l-7.38-5.71c-2.04 1.37-4.65 2.19-8.5 2.19-6.44 0-11.88-3.62-14.46-8.77l-5.89 4.57C7.54 40.12 14.91 46 24 46z"
                    />
                  </svg>
                  <div><strong>Google Business Reviews:</strong> {googleReviews.length}</div>
                </div>
              </div>
            </div>

            {/* === RATINGS CARD === */}


          </div>
        </div>
        {/* Middle Column - Customer List */}
        {/* Middle Column - Customer List (match companyprofile.jsx spacing) */}
<div className="w-full md:w-[48%] bg-white rounded-lg p-4 shadow-[0_0_20px_rgba(0,0,150,0.1)] flex flex-col h-[calc(100vh-7rem)]">

        <h2 className="text-xl font-bold text-[#00aef7] mb-2 text-center">My Customers</h2>
        <div className="flex justify-end -mt-8 mb-1 items-center space-x-1">
        <button
        onClick={handleAddNewCustomer}
        className="text-[#00aef7] text-4xl leading-none hover:text-[#008fc5] transition"
        title="Add New Customer"
        >
        +
        </button>
        <button
          onClick={() => setShowUploadModal(true)}
          className="text-brand  p-2 rounded-full transition-colors mt-[6px]"
          title="Upload Customers"
        >
          <UploadCloud className="h-6 w-6 mt-[2px]" />
        </button> 
        </div>       
        <div className="flex justify-between items-center mb-4">
        </div>
        <div className="pt-4"></div>
          <CustomerCardList
            userId={profile?.u_id}
            businessId={profile?.b_id}
            customers={customers}
            setCustomers={setCustomers}
            fetchReviews={fetchReviews}
          />
        </div>

        {/* Right Column - Reviews & Ratings (match companyprofile.jsx spacing) */}
        <div className="w-full md:w-[28%] bg-white rounded-lg p-4 h-[calc(100vh-7rem)] shadow-[0_0_20px_rgba(0,0,150,0.1)] flex flex-col">
          <div className="top-0 bg-white pb-2 z-10 relative">
            <h2 className="text-xl font-bold text-[#00aef7] mb-2 text-center">
              Ratings & Reviews
            </h2>
            {/* Google refresh icon — floats right, doesn't shift centered text */}
            <button
              onClick={handleGoogleRefreshClick}
              className="absolute right-0 top-0.5 p-1 rounded hover:bg-gray-100 transition-colors"
              title="Update Google Reviews"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 48 48">
                <path fill="#4285F4" d="M24 9.5c3.34 0 5.63 1.44 6.93 2.65l5.08-4.91C32.67 4.02 28.74 2 24 2 14.91 2 7.54 7.88 4.64 15.35l5.89 4.57C12.12 13.12 17.56 9.5 24 9.5z"/>
                <path fill="#34A853" d="M46.5 24c0-1.53-.14-2.99-.4-4.4H24v8.33h12.7c-.55 3.02-2.19 5.58-4.7 7.31l7.38 5.71C43.98 36.9 46.5 30.97 46.5 24z"/>
                <path fill="#FBBC05" d="M10.53 28.74a14.4 14.4 0 0 1 0-9.47l-5.89-4.57a23.953 23.953 0 0 0 0 18.61l5.89-4.57z"/>
                <path fill="#EA4335" d="M24 46c6.48 0 11.91-2.15 15.88-5.86l-7.38-5.71c-2.04 1.37-4.65 2.19-8.5 2.19-6.44 0-11.88-3.62-14.46-8.77l-5.89 4.57C7.54 40.12 14.91 46 24 46z"/>
              </svg>
            </button>
          </div>

          {/* Active filter indicator */}
          {reviewFilter && (
            <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-md px-3 py-1.5 mb-2">
              <span className="text-xs text-red-700 font-medium">
                {reviewFilter === 'not_completed' ? 'Showing: Not Completed' : 'Showing: Not Replied To'}
              </span>
              <button
                onClick={() => setReviewFilter(null)}
                className="text-red-400 hover:text-red-600 ml-2"
                title="Clear filter"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          <div
            className="relative flex-1 min-h-0 overflow-y-auto pl-3 pr-2 rounded-lg scrollbar-thin scrollbar-thumb-gray-100 scrollbar-track-transparent"
            onScroll={handleReviewsScroll}
          >
            <AllReviewsList
              reviews={(() => {
                let filtered;
                if (reviewFilter === 'not_completed') {
                  // Show eHandshake reviews where customer hasn't responded yet
                  filtered = reviews.filter(r => r.customersurvey_google_origin !== 'google_places' && !r.date);
                } else if (reviewFilter === 'not_replied') {
                  // Show completed eHandshake reviews + Google reviews where user hasn't replied
                  filtered = reviews.filter(r => {
                    if (r.customersurvey_google_origin === 'google_places') {
                      return !r.customersurvey_user_reply;
                    }
                    return r.date && !r.customersurvey_user_reply;
                  });
                } else {
                  // Default: show completed reviews only (exclude pending requests)
                  filtered = reviews.filter(r => r.customersurvey_google_origin === 'google_places' || r.date);
                }
                return filtered.slice(0, visibleCount);
              })()}
              showReplyStatus={true}
              onReviewsImported={handleGoogleImportSuccess}
            />
          </div>
        </div>


      </div>
      {showUploadModal && (
        <UploadModal 
          onClose={() => setShowUploadModal(false)}
        onUploadComplete={async ({ success }) => {
          if (success && profile?.u_id) {
            const freshData = await fetch(`/api/customer-cards?user_id=${profile.u_id}`, { credentials: 'include' })
              .then(res => res.json());
            setCustomers(freshData);
          }
        }}

        />
      )}

      {/* Google Refresh Cooldown Modal */}
      {googleRefreshModal === 'cooldown' && googleCooldownInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-bold text-[#00aef7] mb-4">Google Reviews</h3>
            <p className="text-sm text-gray-700 mb-2">
              You can update your Google Reviews every 30 days.
            </p>
            <p className="text-sm text-gray-700 mb-2">
              You last updated your Google Reviews on{' '}
              <strong>
                {new Date(googleCooldownInfo.last_import).toLocaleDateString('en-US', {
                  month: '2-digit', day: '2-digit', year: 'numeric'
                })}
              </strong>.
            </p>
            <p className="text-sm text-gray-700 mb-4">
              You can update again on{' '}
              <strong>
                {new Date(googleCooldownInfo.next_import).toLocaleDateString('en-US', {
                  month: '2-digit', day: '2-digit', year: 'numeric'
                })}
              </strong>.
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setGoogleRefreshModal(null)}
                className="px-4 py-2 bg-[#00aef7] text-white rounded hover:bg-blue-300"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Google Refresh Importing Modal */}
      {googleRefreshModal === 'importing' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 text-center">
            <svg className="animate-spin h-8 w-8 text-[#00aef7] mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-sm text-gray-700">Updating your Google Reviews...</p>
          </div>
        </div>
      )}

      {/* Google First Import Modal (URL input) */}
      {googleRefreshModal === 'first_import' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-bold text-[#00aef7] mb-2">Import Google Reviews</h3>
            <p className="text-xs text-gray-500 mb-4">
              You haven't imported Google Reviews yet. Use the button below to get started.
            </p>
            <GoogleImportButton
              onReviewsImported={handleGoogleImportSuccess}
            />
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setGoogleRefreshModal(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Google Refresh Error Modal */}
      {googleRefreshModal === 'error' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-bold text-red-600 mb-4">Import Error</h3>
            <p className="text-sm text-gray-700 mb-4">{googleRefreshError}</p>
            <div className="flex justify-end">
              <button
                onClick={() => setGoogleRefreshModal(null)}
                className="px-4 py-2 bg-[#00aef7] text-white rounded hover:bg-blue-300"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer Section */}
      <footer className="mt-6"> {/* Adjust margin to control gap */}
        {/* Footer content here */}
      </footer>
    </div>
  );
}

export default CustomerInfo;
